import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarDays,
  CalendarRange,
  Clock,
  FileQuestion,
  FileText,
  Package,
  ShieldAlert,
  Star,
  Stethoscope,
  TrendingUp,
  UserCog,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { useFacilityCurrency } from "@/components/account/facility/facility-currency";
import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";
import { ServicesPausedBanner } from "@/components/billing/services-paused-banner";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";
import { useAppointmentStatusLabel } from "@/lib/appointment-labels";
import { formatMoney } from "@/lib/format-money";

interface FacilityProfile {
  id: string;
  name: Record<string, string> | string | null;
  ratingAvg: string | number | null;
  addressCity: string | null;
  addressCountry: string | null;
}

interface DashboardAppointment {
  id: string;
  startsAt: string;
  status: string;
  consultationName: string | null;
  patientName: string;
  providerName: string;
}

interface DashboardData {
  todayAppointmentCount: number;
  todayRevenue: number;
  /** 7 daily buckets — index 0 = 6 days ago, index 6 = today. */
  weeklyAppointments: number[];
  monthlyAppointmentCount: number;
  activeProviders: number;
  pendingClaimsCount: number;
  lowStockItems: { id: string; name: string; currentStock: number; minStock: number }[];
  unverifiedProviders: number;
  todaySchedule: DashboardAppointment[];
  recentBookings: DashboardAppointment[];
}

/** Facility sub-routes the dashboard links to (all exist in the route tree). */
type FacilityRoute =
  | "/account/facility/$facilityId/calendar"
  | "/account/facility/$facilityId/providers"
  | "/account/facility/$facilityId/staff"
  | "/account/facility/$facilityId/profile"
  | "/account/facility/$facilityId/inventory";

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/dashboard")({
  component: FacilityDashboardPage,
});

function FacilityDashboardPage() {
  const { facilityId } = Route.useParams();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  // Single aggregator call replaces the old /profile + /calendar stitching.
  const statusLabel = useAppointmentStatusLabel();
  const {
    data: dashboard,
    isLoading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
    isFetching: dashboardFetching,
  } = useQuery({
    queryKey: ["api", `/api/account/facility/${facilityId}/dashboard`],
    queryFn: ({ signal }) =>
      apiFetch.get<DashboardData>(`/api/account/facility/${facilityId}/dashboard`, { signal }),
    retry: false,
  });

  // Profile is optional garnish (name, city, rating, currency) — it is gated
  // to manager roles while the dashboard also allows billing/receptionist, so
  // a 403 here must not block the page.
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["api", `/api/account/facility/${facilityId}/profile`],
    queryFn: ({ signal }) =>
      apiFetch
        .get<FacilityProfile>(`/api/account/facility/${facilityId}/profile`, { signal })
        .catch(() => null),
    retry: false,
  });
  const { currency, isLoading: currencyLoading } = useFacilityCurrency(facilityId);

  if (dashboardLoading || profileLoading || currencyLoading) return <DashboardSkeleton />;
  if (dashboardError || !dashboard) {
    // 403/404 = not on this clinic's staff; network/5xx = retryable, never
    // reported as "no access".
    return (
      <FacilityStateCard
        kind={dashboardError ? facilityErrorKind(dashboardError) : "load-failed"}
        facilityId={facilityId}
        onRetry={() => void refetchDashboard()}
        retrying={dashboardFetching}
        onDashboard
      />
    );
  }

  const facilityName = profile ? pickLocalized(profile.name, locale) : null;
  const ratingValue = profile?.ratingAvg != null ? Number(profile.ratingAvg) : null;

  const attentionCount =
    dashboard.pendingClaimsCount + dashboard.unverifiedProviders + dashboard.lowStockItems.length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={<Trans>Dashboard</Trans>}
        description={
          facilityName ? (
            <>
              {facilityName}
              {profile?.addressCity ? ` · ${profile.addressCity}` : ""}
            </>
          ) : (
            <Trans>Overview of your clinic activity</Trans>
          )
        }
      />

      <ServicesPausedBanner entity={{ entityType: "facility", entityId: facilityId }} />

      {/* Quick actions — first, so a receptionist reaches the calendar without
          scrolling past the stats. Chips on mobile, a 2-column grid on sm+. */}
      <section aria-labelledby="facility-quick-actions">
        <h2
          id="facility-quick-actions"
          className="text-foreground sr-only text-sm font-semibold sm:not-sr-only"
        >
          <Trans>Quick actions</Trans>
        </h2>
        <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] sm:mt-3 sm:grid sm:grid-cols-2 sm:gap-3 sm:overflow-visible">
          <QuickAction
            facilityId={facilityId}
            to="/account/facility/$facilityId/calendar"
            label={t`Open calendar`}
            icon={CalendarRange}
            primary
          />
          <QuickAction
            facilityId={facilityId}
            to="/account/facility/$facilityId/providers"
            label={t`Manage providers`}
            icon={Stethoscope}
          />
          <QuickAction
            facilityId={facilityId}
            to="/account/facility/$facilityId/staff"
            label={t`Invite staff`}
            icon={UserCog}
          />
          <QuickAction
            facilityId={facilityId}
            to="/account/facility/$facilityId/profile"
            label={t`Edit clinic profile`}
            icon={FileText}
          />
        </div>
      </section>

      {/* KPI cards — legacy parity: today (all statuses), active providers, rating, monthly */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={t`Today's appointments`}
          value={dashboard.todayAppointmentCount}
          icon={CalendarDays}
          tone="primary"
        />
        <StatCard
          label={t`Active providers`}
          value={dashboard.activeProviders}
          icon={Stethoscope}
          tone="accent"
        />
        <StatCard
          label={t`Rating`}
          value={ratingValue ?? "—"}
          format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
          icon={Star}
          tone="warning"
        />
        <StatCard
          label={t`Appointments this month`}
          value={dashboard.monthlyAppointmentCount}
          icon={TrendingUp}
          tone="success"
        />
      </div>

      {/* Weekly appointments chart + today's revenue */}
      <section className="border-border bg-card rounded-xl border p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-foreground text-sm font-semibold">
              <Trans>Last 7 days</Trans>
            </h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              <Trans>Appointments</Trans>
            </p>
          </div>
          <div className="text-right">
            <p className="text-muted-foreground text-xs font-medium">
              <Trans>Revenue today</Trans>
            </p>
            <p className="text-foreground mt-0.5 text-lg font-bold tabular-nums">
              {formatMoney(dashboard.todayRevenue, currency, locale, { maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>
        <WeeklyChart weekly={dashboard.weeklyAppointments} locale={locale} />
      </section>

      {/* Needs attention — pending claims / unverified providers / low stock */}
      {attentionCount > 0 && (
        <section className="border-border bg-card rounded-xl border p-5">
          <h2 className="text-foreground flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="text-warning size-4" aria-hidden />
            <Trans>Needs attention</Trans>
          </h2>
          <ul className="divide-border mt-3 divide-y">
            {/* Claim requests are reviewed by PontiMed admins — the clinic has
                nothing to act on, so this row is a status, not a link. */}
            {dashboard.pendingClaimsCount > 0 && (
              <AttentionRow
                icon={FileQuestion}
                label={t`Profile claim under review by PontiMed`}
                count={dashboard.pendingClaimsCount}
              />
            )}
            {dashboard.unverifiedProviders > 0 && (
              <AttentionRow
                icon={ShieldAlert}
                label={t`Unverified providers`}
                count={dashboard.unverifiedProviders}
                facilityId={facilityId}
                to="/account/facility/$facilityId/providers"
              />
            )}
            {dashboard.lowStockItems.length > 0 && (
              <AttentionRow
                icon={Package}
                label={t`Low stock`}
                count={dashboard.lowStockItems.length}
                facilityId={facilityId}
                to="/account/facility/$facilityId/inventory"
                detail={dashboard.lowStockItems
                  .slice(0, 3)
                  .map((item) => `${item.name} (${item.currentStock}/${item.minStock})`)
                  .join(" · ")}
              />
            )}
          </ul>
        </section>
      )}

      {/* Today's schedule */}
      <section className="border-border bg-card rounded-xl border p-5">
        <h2 className="text-foreground flex items-center gap-2 text-sm font-semibold">
          <Clock className="text-primary size-4" aria-hidden />
          <Trans>Today's schedule</Trans>
        </h2>
        {dashboard.todaySchedule.length === 0 ? (
          <div className="border-border mt-4 flex min-h-[160px] items-center justify-center rounded-lg border-2 border-dashed p-6">
            <div className="text-center">
              <CalendarDays className="text-muted-foreground/40 mx-auto size-8" aria-hidden />
              <p className="text-muted-foreground mt-2 text-sm">
                <Trans>No appointments today</Trans>
              </p>
            </div>
          </div>
        ) : (
          <ul className="border-border divide-border mt-4 divide-y overflow-hidden rounded-lg border">
            {dashboard.todaySchedule.map((appt) => {
              const time = new Date(appt.startsAt).toLocaleTimeString(locale, {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });
              return (
                <li key={appt.id}>
                  {/* Opens the appointments list filtered to that day — the
                      UTC day, which is what that page compares `?date=` to. */}
                  <Link
                    to="/account/facility/$facilityId/appointments"
                    params={{ facilityId }}
                    search={{ date: new Date(appt.startsAt).toISOString().slice(0, 10) }}
                    className="hover:bg-muted/40 flex min-h-11 items-center gap-3 px-3 py-2 transition-colors"
                  >
                    <span className="text-foreground min-w-[3rem] text-sm font-semibold tabular-nums">
                      {time}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-sm font-medium">
                        {appt.patientName || <Trans>Patient</Trans>}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {appt.providerName}
                        {appt.consultationName ? ` · ${appt.consultationName}` : ""}
                      </p>
                    </div>
                    <Badge variant={statusVariant(appt.status)} className="shrink-0">
                      {statusLabel(appt.status)}
                    </Badge>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Recent activity — 5 most recently created bookings */}
      <section className="border-border bg-card rounded-xl border p-5">
        <h2 className="text-foreground text-sm font-semibold">
          <Trans>Recent activity</Trans>
        </h2>
        {dashboard.recentBookings.length === 0 ? (
          <div className="border-border mt-4 flex min-h-[120px] items-center justify-center rounded-lg border-2 border-dashed p-6">
            <p className="text-muted-foreground text-sm">
              <Trans>No recent activity yet.</Trans>
            </p>
          </div>
        ) : (
          <ul className="divide-border mt-2 divide-y">
            {dashboard.recentBookings.slice(0, 5).map((appt) => {
              const when = new Date(appt.startsAt);
              const dateLabel = when.toLocaleDateString(locale, { day: "2-digit", month: "short" });
              const timeLabel = when.toLocaleTimeString(locale, {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });
              return (
                <li key={appt.id} className="flex items-start gap-3 py-3">
                  <CalendarDays className="text-primary mt-0.5 size-4 shrink-0" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-sm font-medium">
                      {appt.patientName || <Trans>Patient</Trans>}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">
                      {dateLabel} {timeLabel} · {appt.providerName}
                    </p>
                  </div>
                  <Badge variant={statusVariant(appt.status)} className="shrink-0">
                    {statusLabel(appt.status)}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <AccountSecurityBanner />
    </div>
  );
}

function WeeklyChart({ weekly, locale }: { weekly: number[]; locale: string }) {
  const max = Math.max(...weekly, 1);
  const hasAny = weekly.some((count) => count > 0);

  if (!hasAny) {
    return (
      <div className="border-border mt-4 flex h-[140px] items-center justify-center rounded-lg border-2 border-dashed">
        <p className="text-muted-foreground text-sm">
          <Trans>No appointments</Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 flex h-[140px] items-end gap-2">
      {weekly.map((count, i) => {
        const day = new Date();
        day.setDate(day.getDate() - (6 - i));
        const label = day.toLocaleDateString(locale, { weekday: "short" });
        const heightPct = Math.round((count / max) * 100);
        return (
          <div key={i} className="flex flex-1 flex-col items-center gap-1">
            <span className="text-muted-foreground text-xs font-medium tabular-nums">
              {count > 0 ? count : ""}
            </span>
            <div className="bg-muted flex w-full items-end rounded-t-sm" style={{ height: "96px" }}>
              <div
                className="bg-primary w-full rounded-t-sm transition-all duration-500"
                style={{ height: `${Math.max(heightPct, count > 0 ? 4 : 0)}%` }}
              />
            </div>
            <span className="text-muted-foreground text-xs">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function AttentionRow({
  icon: Icon,
  label,
  count,
  facilityId,
  to,
  detail,
}: {
  icon: LucideIcon;
  label: string;
  count: number;
  /** The row links to `to` when both are set; otherwise it is a status row. */
  facilityId?: string;
  to?: FacilityRoute;
  detail?: string;
}) {
  const content = (
    <>
      <Icon className="text-warning-strong mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-foreground text-sm font-medium">{label}</p>
        {detail && <p className="text-muted-foreground mt-0.5 truncate text-xs">{detail}</p>}
      </div>
      <Badge variant="warning" className="shrink-0 tabular-nums">
        {count}
      </Badge>
      {to && facilityId && (
        <ArrowUpRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
      )}
    </>
  );

  return (
    <li>
      {to && facilityId ? (
        <Link
          to={to}
          params={{ facilityId }}
          className="hover:bg-muted/40 flex min-h-11 items-start gap-3 py-3"
        >
          {content}
        </Link>
      ) : (
        <div className="flex min-h-11 items-start gap-3 py-3">{content}</div>
      )}
    </li>
  );
}

function QuickAction({
  facilityId,
  to,
  label,
  icon: Icon,
  primary = false,
}: {
  facilityId: string;
  to: FacilityRoute;
  label: string;
  icon: LucideIcon;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      params={{ facilityId }}
      className={cn(
        "flex min-h-11 shrink-0 items-center justify-between gap-2 rounded-lg border px-3 whitespace-nowrap transition-colors sm:min-h-[56px] sm:px-4 sm:py-3",
        primary
          ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
          : "border-border bg-card text-foreground hover:bg-muted/40",
      )}
    >
      <span className="flex items-center gap-2.5">
        <Icon
          className={cn("size-4", primary ? "text-primary-foreground" : "text-muted-foreground")}
          aria-hidden
        />
        <span className="text-sm font-medium">{label}</span>
      </span>
      <ArrowUpRight
        className={cn(
          "hidden size-4 sm:block",
          primary ? "text-primary-foreground" : "text-muted-foreground",
        )}
        aria-hidden
      />
    </Link>
  );
}

function statusVariant(
  status: string,
): "secondary" | "success" | "warning" | "destructive" | "neutral" {
  switch (status) {
    case "confirmed":
      return "secondary";
    case "completed":
      return "success";
    case "cancelled":
      return "destructive";
    case "pending":
      return "warning";
    default:
      return "neutral";
  }
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-56 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}

function pickLocalized(
  value: Record<string, string> | string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as Record<string, string>;
      return parsed[locale] ?? parsed.ro ?? parsed.en ?? null;
    } catch {
      return value;
    }
  }
  return value[locale] ?? value.ro ?? value.en ?? null;
}
